import React from 'react';
import { H2, P, UL, LI, Code, Callout } from './_shared';

export default {
  slug: 'folder-lifecycle',
  title: 'Folder lifecycle states',
  category: 'folders',
  summary: 'Draft → active → in_escrow → pending_review → closed → archived. What each state means and when it transitions.',
  searchTerms: ['folder', 'lifecycle', 'status', 'states', 'escrow', 'archived'],
  related: ['folders-intro', 'compliance'],
  Body: () => (
    <>
      <P>
        Every folder moves through six states. They drive what the AI suggests,
        what shows up on the broker dashboard, and what's eligible for the
        retention archive.
      </P>

      <H2>The states</H2>
      <UL>
        <LI><Code>draft</Code> — created but no docs sent yet. Editing is unrestricted.</LI>
        <LI><Code>active</Code> — at least one envelope sent. Most folders live here.</LI>
        <LI><Code>in_escrow</Code> — purchase agreement fully signed; closing process underway.</LI>
        <LI><Code>pending_review</Code> — closing docs signed, awaiting broker compliance sign-off.</LI>
        <LI><Code>closed</Code> — deal complete, commission paid, all docs filed. Read-only for agents.</LI>
        <LI><Code>archived</Code> — moved to long-term storage after the retention window. Read-only for everyone but compliance.</LI>
      </UL>

      <H2>Auto-transitions vs manual</H2>
      <P>
        Most transitions happen automatically based on activity:
      </P>
      <UL>
        <LI><Code>draft → active</Code>: first envelope sent.</LI>
        <LI><Code>active → in_escrow</Code>: purchase agreement signed by all parties.</LI>
        <LI><Code>in_escrow → pending_review</Code>: deal stage set to "Closed Won."</LI>
        <LI><Code>pending_review → closed</Code>: broker clicks "Approve" on the compliance checklist.</LI>
        <LI><Code>closed → archived</Code>: 7-year retention timer expires.</LI>
      </UL>

      <Callout kind="warn">
        Once a folder is <Code>closed</Code>, agents can no longer edit it.
        That's intentional — it prevents anyone from changing the file after
        the broker has signed off. If you genuinely need to fix something,
        ask your broker to reopen it via Broker → Audit.
      </Callout>
    </>
  ),
};
